import { Icon } from '@iconify/react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useAddPurchasesLicenseApiMutation } from '../redux/api/getLicenseProductsApi'
import { formatAmount } from '../utilities/helpers'
import { PurchaseDetailsModalLabel } from '../utilities/pageConstants'
import { commonLabelName } from '../utilities/commonConstants'

/**
 * Type definition for License object.
 */

type License = {
    ProductCode: string
    ProductName: string
    Price?: number | string | undefined
    UiPrice: number | string | undefined
}

const PurchaseDetailsModal = ({
    licenses,
    quantities,
    onClose,
    type,
    setMainTab,
    setSelectedLicenses,
    setQuantities,
    setSelectedLicensesDetails
}: any) => {
    const navigate = useNavigate()
    // Prevent background scroll when the modal is open
    useEffect(() => {
        document.body.classList.add('no-scroll')
        return () => {
            document.body.classList.remove('no-scroll')
        }
    }, [])

    const [addPurchasesLicenseApi, { isLoading }] = useAddPurchasesLicenseApiMutation()
    /**
     * Handles the confirmation of a purchase.
     * Sends purchase details to the API and navigates to the home page on success.
     */
    const onConfirm = () => {
        const lineItems = licenses.map((license: License) => {
            console.log(license?.ProductName, license?.UiPrice, 'license?.UiPrice')
            console.log(license?.ProductName, license?.Price, 'license?.Price')

            const quantity = quantities[license?.ProductCode] || 0
            const price = license?.Price === 'Free' || '' ? 0 : Number(license?.Price) || 0
            const priceWithMaintenance = Number(license?.UiPrice) || Number(license?.Price)

            const item = {
                productPartNumber: license?.ProductCode,
                productName: license?.ProductName,
                quantity: quantity,
                price: price,
                priceWithMaintenance,
                maintenancePrice: type === 'ACADEMIC,TRAINING' ? 0 : priceWithMaintenance - price
            }

            return item
        })

        const payload = {
            type: type,
            lineItems: lineItems
        }

        addPurchasesLicenseApi(payload)
            .unwrap()
            .then((res) => {
                // Only navigate if the API call was successful
                if (res.message === 'success') {
                    toast.success(
                        'Your purchase intent has been notified to the sales team. Our sales team will work on the next steps and inform you.'
                    )
                    onClose()
                    if (setQuantities) setQuantities({})
                    if (setSelectedLicenses) setSelectedLicenses(new Set())
                    if (setSelectedLicensesDetails) setSelectedLicensesDetails([])
                    if (setMainTab) setMainTab('productDownloads')
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                    localStorage.setItem('fromProfile', 'true')
                }
            })
            .catch((error) => {
                console.error('Error adding purchase:', error)
                toast.error('There was an error processing your purchase. Please try again.')
            })
    }

    return (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center'>
            <div className='bg-white p-6 rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col'>
                <div className='flex justify-between items-center'>
                    <h2 className='text-lg font-semibold'>{PurchaseDetailsModalLabel?.purchaseDetails}</h2>
                    <Icon
                        icon='radix-icons:cross-2'
                        className='text-red justify-end items-end cursor-pointer'
                        width='24'
                        height='24'
                        onClick={onClose}
                    />
                </div>
                <div className='mt-4 border-2 bg-[#fafcfa] border-gray p-4 rounded-lg flex-1 overflow-auto'>
                    <div className='overflow-x-auto'>
                        <table className='w-full mt-4 border-2 border-gray rounded-lg'>
                            <thead>
                                <tr className='bg-gray-100 border-b-2 bg-white border-gray text-secondary'>
                                    {PurchaseDetailsModalLabel?.tableHeaders.map((header) => (
                                        <th key={header} className='p-2 text-left'>
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {licenses.map((license: License, idx: number) => (
                                    <tr
                                        key={license?.ProductCode}
                                        className={`${idx % 2 === 0 ? 'bg-[#fafcfa]' : 'bg-white'} border-b-2 border-gray text-secondary`}
                                    >
                                        <td className='p-4 text-left'>{license?.ProductName}</td>
                                        <td className='p-4 text-start'>{quantities[license?.ProductCode]?.toString().padStart(2, '0')}</td>
                                        <td className='p-4 text-center'>
                                            {license?.UiPrice === 0 ||
                                            license?.Price === 0 ||
                                            license?.UiPrice === undefined ||
                                            license?.Price === undefined ||
                                            license?.UiPrice === 'Free' ||
                                            license?.Price === 'Free'
                                                ? `$ ${0}`
                                                : `$ ${formatAmount(
                                                      Number(license?.UiPrice || license?.Price) * quantities[license?.ProductCode]
                                                  )}`}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className='flex justify-end mt-4 font-semibold'>
                            {PurchaseDetailsModalLabel?.grandTotal}
                            <span className='text-primary ml-4'>
                                {`$ ${formatAmount(
                                    licenses.reduce((total: number, license: License) => {
                                        const quantity = quantities[license?.ProductCode] || 0
                                        const price =
                                            license?.UiPrice === 0 ||
                                            license?.Price === 0 ||
                                            license?.UiPrice === undefined ||
                                            license?.Price === undefined ||
                                            license?.UiPrice === 'Free' ||
                                            license?.Price === 'Free'
                                                ? 0
                                                : Number(license?.UiPrice || license?.Price)
                                        return total + price * quantity
                                    }, 0)
                                )}`}
                            </span>
                        </div>
                    </div>
                </div>
                <div className='flex justify-center mt-4'>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`flex items-center px-12 py-2 bg-primary text-white rounded-md font-semibold ${
                            isLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
                        }`}
                    >
                        {commonLabelName?.confirm}
                        {isLoading && <span className='loader'></span>}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default PurchaseDetailsModal
